import dayjs from 'dayjs'
import { getOpenInterestChange } from '../../services/apis/participant'
import { ExpiryOpenInterestProto } from '../../proto/generated/open-interest'
import { Exchange } from '../../proto/generated/common'
import { TradingCalendarData } from '../../proto/generated/trading-calendar'
import { getPreviousValidTradingDay } from '../../util/tradingCalendar'

/** Shared `/v2/options/oichange` window resolution + in-flight/snapshot cache for OI Analysis screens. */

const MS_PER_MINUTE = 60_000

export type OiWindow = { fromTime: number; toTime: number }

/** API `toTime` excludes the session/day end instant. */
export function toTimeExclusiveEnd(fromTime: number, rawEndInclusive: number): number {
    return Math.max(fromTime, rawEndInclusive - MS_PER_MINUTE)
}

const windowEndingAt = (fromTime: number, rawEndInclusive: number): OiWindow => ({
    fromTime,
    toTime: toTimeExclusiveEnd(fromTime, rawEndInclusive),
})

export type OiWindowInput = {
    /** Day being analysed. */
    analysisDay: dayjs.Dayjs
    /** Latest day the exchange actually traded, from the trading calendar. */
    effectiveTradingDate: Date | number | string | undefined
    /** Trading session bounds for `analysisDay` as `[startMs, endMs]` pairs. */
    sessionHours: number[][]
    /** Window picked on the intraday slider. Only honoured while the day is a live session day. */
    sliderWindow?: { from: number | null; to: number | null } | null
    /** Fall back to calendar-day bounds instead of session bounds (OI Change intraday behaviour). */
    dayBounded?: boolean
    /** Session bounds for the trading day before `analysisDay`, as `[startMs, endMs]` pairs. */
    previousTradingDaySessionHours?: number[][]
    now?: number
}

/** Last session close of the previous trading day, minus 1 minute so `from` stays on that day. */
export function fromTimeAtPreviousClose(sessionHours: number[][] | undefined): number | undefined {
    if (!sessionHours?.length) return undefined
    return Math.max(...sessionHours.map((s) => s[1])) - MS_PER_MINUTE
}

export function previousTradingDaySessionHours(
    analysisDay: dayjs.Dayjs,
    calendarEvents: TradingCalendarData | undefined,
    getSessionHoursForDate: (date: dayjs.Dayjs) => number[][]
): number[][] {
    const prev = getPreviousValidTradingDay(new Date(analysisDay.valueOf()), calendarEvents)
    if (!prev) return []
    return getSessionHoursForDate(dayjs(prev))
}

/**
 * Resolve the `fromTime`/`toTime` an OI request should use.
 *
 * `fromTime` is the previous trading day's market close minus 1 minute whenever that close is known
 * (slider window, session bounds, or day bounds). `toTime` still follows the slider / session / day end.
 */
export function resolveOiWindow(input: OiWindowInput): OiWindow {
    const {
        analysisDay,
        effectiveTradingDate,
        sessionHours,
        sliderWindow,
        dayBounded = false,
        previousTradingDaySessionHours,
    } = input
    const now = input.now ?? Date.now()
    const previousCloseFrom = fromTimeAtPreviousClose(previousTradingDaySessionHours)

    const isLiveSessionDay =
        analysisDay.isSame(dayjs(now), 'day') && dayjs(now).isSame(dayjs(effectiveTradingDate), 'day')

    if (isLiveSessionDay && sliderWindow?.from != null && sliderWindow?.to != null) {
        return windowEndingAt(previousCloseFrom ?? sliderWindow.from, sliderWindow.to)
    }

    if (!dayBounded && sessionHours.length) {
        const fromTime = previousCloseFrom ?? Math.min(...sessionHours.map((s) => s[0]))
        const sessionIsOpen =
            isLiveSessionDay && sessionHours.some(([start, end]) => now >= start && now <= end)
        const rawEnd = sessionIsOpen
            ? dayjs(now).startOf('minute').valueOf()
            : Math.max(...sessionHours.map((s) => s[1]))
        return windowEndingAt(fromTime, rawEnd)
    }

    const fromTime = previousCloseFrom ?? analysisDay.startOf('day').valueOf()
    return windowEndingAt(fromTime, analysisDay.endOf('day').valueOf())
}

/** Multi-day window: open of the first day's session to the close of the last day's. */
export function resolveOiCustomRangeWindow(
    fromDay: dayjs.Dayjs,
    toDay: dayjs.Dayjs,
    fromSessionHours: number[][],
    toSessionHours: number[][]
): OiWindow {
    const fromTime = fromSessionHours.length
        ? Math.min(...fromSessionHours.map((s) => s[0]))
        : fromDay.startOf('day').valueOf()
    const rawEnd = toSessionHours.length
        ? Math.max(...toSessionHours.map((s) => s[1]))
        : toDay.endOf('day').valueOf()
    return windowEndingAt(fromTime, rawEnd)
}

export type OiSnapshotRequest = {
    security: { code: string; exchange: Exchange }
    /** Empty array asks for every expiry in the chain. */
    expiries: number[]
    window: OiWindow
    /**
     * Bucket to remember the result under. A screen that remounts can paint the previous rows via
     * `readOiSnapshotMemory` instead of an empty chart while its own refetch is in flight.
     */
    memoryKey?: string
}

const inFlight = new Map<string, Promise<ExpiryOpenInterestProto[]>>()
const snapshotMemory = new Map<string, ExpiryOpenInterestProto[]>()
const SNAPSHOT_MEMORY_LIMIT = 8

export function oiSnapshotMemoryKey(screen: string, securityKey: string): string {
    return `${screen}|${securityKey}`
}

export function readOiSnapshotMemory(memoryKey: string | undefined): ExpiryOpenInterestProto[] | undefined {
    return memoryKey ? snapshotMemory.get(memoryKey) : undefined
}

function remember(memoryKey: string, rows: ExpiryOpenInterestProto[]) {
    snapshotMemory.delete(memoryKey)
    snapshotMemory.set(memoryKey, rows)
    while (snapshotMemory.size > SNAPSHOT_MEMORY_LIMIT) {
        const oldest = snapshotMemory.keys().next().value
        if (oldest === undefined) break
        snapshotMemory.delete(oldest)
    }
}

/** Identical concurrent requests share one network call. */
export async function fetchOiSnapshot({
    security,
    expiries,
    window,
    memoryKey,
}: OiSnapshotRequest): Promise<ExpiryOpenInterestProto[]> {
    const requestKey = [
        security.exchange,
        security.code,
        expiries.join(','),
        window.fromTime,
        window.toTime,
    ].join('|')

    const pending = inFlight.get(requestKey)
    if (pending) return pending

    const request = getOpenInterestChange({
        security: { code: security.code, exchange: security.exchange },
        expiries,
        fromTime: window.fromTime,
        toTime: window.toTime,
    })
        .then((response) => {
            const rows = response?.expiryOpenInterest ?? []
            if (memoryKey) remember(memoryKey, rows)
            return rows
        })
        .finally(() => {
            inFlight.delete(requestKey)
        })

    inFlight.set(requestKey, request)
    return request
}

const byExpiryId = (a: string, b: string) => Number(a) - Number(b)

/** Expiry ids present in the response, and the subset that actually carries an option chain. */
export function expiryIdsFromSnapshot(rows: ExpiryOpenInterestProto[]) {
    const all = [...new Set(rows.map((e) => e.expiry.toString()))].sort(byExpiryId)
    const withChain = [
        ...new Set(
            rows.filter((e) => e.strikeOptionChainLineItem.length !== 0).map((e) => e.expiry.toString())
        ),
    ].sort(byExpiryId)
    return { all, withChain }
}

/**
 * Prefer the options-chain expiry Overview uses for multistrike legs, so bar tiles and line tiles
 * cannot drift onto different contracts. Falls back to the nearest chain expiry in the snapshot.
 */
export function resolvePreferredBarExpiryIds(
    rows: ExpiryOpenInterestProto[],
    preferred?: { expiry?: number; name?: string } | null
): string[] {
    const { withChain } = expiryIdsFromSnapshot(rows)
    if (!withChain.length) return []
    const candidates = [
        preferred?.expiry != null ? String(preferred.expiry) : undefined,
        preferred?.name,
    ].filter((id): id is string => !!id)
    for (const id of candidates) {
        if (withChain.includes(id)) return [id]
    }
    return [withChain[0]]
}
